import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown, { defaultUrlTransform } from "react-markdown";
import { useSearchParams } from "react-router";

import { Dropdown } from "capitalrx-components-2.0/lib/forms";
import { Button, Icon, Message, Messages } from "capitalrx-components-2.0/lib/general";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subtitle, Underlined } from "capitalrx-components-2.0/lib/typography";
import styled from "styled-components";

import { useListMedicarePrescriptionPaymentPlanDueInvoicesQuery } from "@/App/api/medicarePrescriptionPaymentPlan";
import { ClientAwareLink, ContentPanel, PageWrapper, ScreenReader, ThemedTabbedContainer } from "@/App/components";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { medicarePartDStrategies } from "@/App/constants/constants";
import { ExperienceContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useNavigateClient } from "@/App/hooks";

import { ParticipationTab } from "./Participation";
import { PaymentCalculatorTab } from "./PaymentCalculator";

import "./MedicarePrescriptionPaymentPlan.scss";

const ToggleShowButton = styled.span`
  cursor: pointer;
`;

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.medicarePrescriptionPaymentPlanPage";

const ParticipationTabLabel: FC<{ planYear: number, tab: string }> = (props) => {
  const { t: translate } = useTranslation();
  const { data: dueInvoices, isLoading: isLoadingDueInvoices } = useListMedicarePrescriptionPaymentPlanDueInvoicesQuery(props.planYear);

  let showIcon = false;
  if (props.planYear === 2025 && !isLoadingDueInvoices && dueInvoices.results.length > 0 && props.tab !== "participation-" + props.planYear) {
    showIcon = true;
  }
  return (
    <>
      <Shelf>
        {translate(`${TRANSLATION_PATH}.participationDetails`) + ` ${props.planYear}`}
        {showIcon && <Icon name="warning-circle" color={Color.Yellow["700"]} />}
        <Shelf.Spacer />
      </Shelf>
    </>
  );
};

const PaymentDueCallToAction: FC<{ planYear: number, tab: string }> = (props) => {
  const { t: translate } = useTranslation();
  const { data: dueInvoices, isLoading: isLoadingDueInvoices } = useListMedicarePrescriptionPaymentPlanDueInvoicesQuery(props.planYear);
  const navigate = useNavigateClient();

  const handleClickPay = useCallback((year: number) => {
    navigate(`/medicare-prescription-payment-plan?tab=participation-${year}`);
  }, []);

  return <>
    {!isLoadingDueInvoices && (
      <Message visible={dueInvoices.results.length > 0 && props.tab !== "participation-" + props.planYear} appearance={Messages.Warning}>
        <StackOrShelf>
          <Body>{translate(`${TRANSLATION_PATH}.outstandingBalanceWarning`).replaceAll("{year}", "" + props.planYear)}</Body>
          <Shelf.Spacer />
          <Shelf>
            <Button style={{ padding: "0" }} appearance="text" onClick={() => handleClickPay(props.planYear)}>
              {translate(`${TRANSLATION_PATH}.reviewAndPay`)}
            </Button>
            <Shelf.Spacer />
          </Shelf>
        </StackOrShelf>
      </Message>
    )}
  </>;
};

export const MedicarePrescriptionPaymentPlanPage: FC = () => {
  const [hasPartD, setHasPartD] = useState(false);
  const [isShowingMoreDescription, setIsShowingMoreDescription] = useState(false);

  const { t: translate } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "calculator";

  const { memberEligibility } = useContext(MemberEligibilityContext);
  const {
    medicarePrescriptionPaymentPlanEmailSection,
    medicarePrescriptionPaymentPlanMailSection,
    medicarePrescriptionPaymentPlanOnlineSection,
    medicarePrescriptionPaymentPlanPhoneSection,
    medicarePrescriptionPaymentPlanExplanationSection,
  } = useContext(ExperienceContext);

  const {
    planYears,
    firstOpenPlanYear,
  } = useMemo(() => {
    return {
      planYears: (memberEligibility.mppp_plan_year_bounds || []).map((x) => x.plan_year),
      firstOpenPlanYear: (memberEligibility.mppp_plan_year_bounds || []).find((x) => x.is_open)?.plan_year,
    };
  }, [memberEligibility.mppp_plan_year_bounds]);

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const onSelectTab = useCallback(
    (tab: string) => {
      const clientParam = searchParams.get("client");
      if (clientParam) {
        setSearchParams({ client: clientParam, tab: tab });
      } else {
        setSearchParams({ tab: tab });
      }
    },
    [searchParams],
  );

  useEffect(() => {
    if (memberEligibility?.plan_benefit) {
      setHasPartD(medicarePartDStrategies.includes(memberEligibility?.plan_benefit?.coverage_strategy_type));
    }
  }, [memberEligibility]);

  const programDescription = useMemo(() => {
    return (
      <Body className={!isShowingMoreDescription ? "collapsed" : ""}>
        <Markdown>{medicarePrescriptionPaymentPlanExplanationSection}</Markdown>
        {(medicarePrescriptionPaymentPlanOnlineSection
          || medicarePrescriptionPaymentPlanEmailSection
          || medicarePrescriptionPaymentPlanPhoneSection
          || medicarePrescriptionPaymentPlanMailSection) && <b>{translate(`${TRANSLATION_PATH}.howDoISignUp`)}</b>}
        {medicarePrescriptionPaymentPlanOnlineSection && <Markdown>{medicarePrescriptionPaymentPlanOnlineSection}</Markdown>}
        {medicarePrescriptionPaymentPlanEmailSection && <Markdown>{medicarePrescriptionPaymentPlanEmailSection}</Markdown>}
        {medicarePrescriptionPaymentPlanPhoneSection && (
          <Markdown urlTransform={(url) => (url.startsWith("tel:") ? url : defaultUrlTransform(url))}>
            {medicarePrescriptionPaymentPlanPhoneSection}
          </Markdown>
        )}
        {medicarePrescriptionPaymentPlanMailSection && <Markdown>{medicarePrescriptionPaymentPlanMailSection}</Markdown>}
      </Body>
    );
  }, [isShowingMoreDescription]);

  return (
    <PageWrapper>
      <ContentPanel
        showBorder={false}
        title={
          <Stack>
            <Subtitle>{translate(`${TRANSLATION_PATH}.medicarePrescriptionPaymentPlanMPPP`)}</Subtitle>
            {hasPartD && (
              <>
                {programDescription}
                <Underlined>
                  <ScreenReader>{translate(`${TRANSLATION_PATH}.showMoreInformation`)}</ScreenReader>
                  <ToggleShowButton data-testid="toggle-show" onClick={() => setIsShowingMoreDescription(!isShowingMoreDescription)}>
                    {!isShowingMoreDescription && translate(`${TRANSLATION_PATH}.showMore`)}
                    {isShowingMoreDescription && translate(`${TRANSLATION_PATH}.showLess`)}
                  </ToggleShowButton>
                </Underlined>
              </>
            )}
          </Stack>
        }
      >
        <Stack spacing="large">
          <Stack>
            {planYears.map((planYear) => {
              return <PaymentDueCallToAction key={"mppp-pay-call-to-action-" + planYear} planYear={planYear} tab={tab} />;
            })}
          </Stack>
          {hasPartD && (
            <>
              {!isCompressed && (
                <ThemedTabbedContainer
                  testId="medicarePrescriptionPaymentPlan-actions-tab-container"
                  selectedTab={tab}
                  onSelectedTabChange={onSelectTab}
                >
                  <ThemedTabbedContainer.TabPane
                    keepHiddenContentInDOM
                    testId="payment-calculator-tab"
                    id="calculator"
                    label={translate(`${TRANSLATION_PATH}.paymentCalculator`)}
                  >
                    <PaymentCalculatorTab onOptIn={() => onSelectTab("participation-" + firstOpenPlanYear)} />
                  </ThemedTabbedContainer.TabPane>
                  {planYears.map((planYear) => {
                    return (
                      <ThemedTabbedContainer.TabPane
                        keepHiddenContentInDOM
                        testId={`participation-tab-${planYear}`}
                        id={`participation-${planYear}`}
                        label={<ParticipationTabLabel planYear={planYear} tab={tab} />}
                        key={`participation-${planYear}`}
                      >
                        <ParticipationTab planYear={planYear} />
                      </ThemedTabbedContainer.TabPane>
                    );
                  })}
                </ThemedTabbedContainer>
              )}
              {isCompressed && (
                <Stack spacing="large">
                  <Dropdown
                    value={tab}
                    onChange={(value) => onSelectTab(value.toString())}
                    hideLabel
                    placeholder={translate(`${TRANSLATION_PATH}.select`)}
                  >
                    <Dropdown.Option value="calculator">{translate(`${TRANSLATION_PATH}.paymentCalculator`)}</Dropdown.Option>
                    {planYears.map((planYear) => {
                      return (
                        <Dropdown.Option value={`participation-${planYear}`} key={`participation-${planYear}`}>
                          <ParticipationTabLabel planYear={planYear} tab={tab} />
                        </Dropdown.Option>
                      );
                    })}
                  </Dropdown>
                  <>
                    {tab === "calculator" && <PaymentCalculatorTab onOptIn={() => onSelectTab("participation-" + firstOpenPlanYear)} />}
                    {planYears.map((planYear) => {
                      return tab === `participation-${planYear}` && <ParticipationTab planYear={planYear} key={`tab-${planYear}`} />;
                    })}
                  </>
                </Stack>
              )}
            </>
          )}
          {!hasPartD && (
            <Body>
              {translate(`${TRANSLATION_PATH}.memberNotPartOfPartDNotice`)}{" "}
              <ClientAwareLink to="/">{translate(`${TRANSLATION_PATH}.returnHome`)}</ClientAwareLink>.
            </Body>
          )}
        </Stack>
      </ContentPanel>
    </PageWrapper>
  );
};
